import { useCallback, useContext } from 'react';
import AccountPickerContext from '../../../../../../contexts/AccountPickerContext';
import DashboardGadgetsContext from '../../../../../../contexts/DashboardGadgetsContext';
import STORE from '../../../../../../store';
import MetricListsContext from '../../../../../../contexts/MetricListsContext';
import isDefined from '../../../../../../isDefined';
import replaceMetricInVis from './replaceMetricInVis';
import { FindAndReplace } from '../../types';

const useFindAndReplaceDashboardGadgets = () => {
  const { allGadgets: gadgets } = useContext(DashboardGadgetsContext);
  const { metricLists } = useContext(MetricListsContext);
  const { selectedAccountId } = useContext(AccountPickerContext);

  const replaceGadgets = useCallback(
    async (metricReceipt: FindAndReplace.MetricReceipt) => {
      const { findMetricId, replaceMetricId } = metricReceipt;
      const affectedGadgetIds =
        metricReceipt?.affectedContentIds.dashboardGadgets;

      if (!affectedGadgetIds?.length) {
        return;
      }

      const metricListPromises: Promise<void>[] = [];
      metricLists.forEach((metricList) => {
        // Selected dashboard gadget is of type metricList
        if (affectedGadgetIds.includes(metricList.id)) {
          const clonedMetricList = structuredClone(metricList);
          clonedMetricList.list
            .filter((l) => l.metricId === findMetricId)
            .forEach((l) => {
              l.metricId = replaceMetricId;

              metricListPromises.push(
                STORE.visualisations
                  .getMetricListsRef({ accountId: selectedAccountId })
                  .doc(clonedMetricList.id)
                  .set(clonedMetricList),
              );
            });
        }
      });
      if (metricListPromises.length) {
        await Promise.all(metricListPromises);
      }

      const gadgetPromises = affectedGadgetIds
        .map((gadgetId) => gadgets.find((g) => g.id === gadgetId))
        .filter(isDefined)
        .map((gadget) => {
          const clonedGadget = structuredClone(gadget);
          clonedGadget.chartDef = replaceMetricInVis(
            clonedGadget.chartDef,
            findMetricId,
            replaceMetricId,
          );
          return STORE.visualisations
            .getDashboardGadgetsRef({ accountId: selectedAccountId })
            .doc(clonedGadget.id)
            .set(clonedGadget);
        });

      await Promise.all(gadgetPromises);
    },
    [gadgets, metricLists, selectedAccountId],
  );

  return replaceGadgets;
};

export default useFindAndReplaceDashboardGadgets;
